import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import ChevronLeftLargeIcon from '@atlaskit/icon/utility/migration/chevron-left--chevron-left-large';
import SearchIcon from '@atlaskit/icon/core/migration/search';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { TRACK_EVENT_TYPE } from '../../../../common/constants.tsx';
import type { CommandInternal, ListedCommand } from '../../../../common/types/commands/index.tsx';
import { CommandPaletteSearchInput } from '../../../../common/ui/command-palette-search-dropdown/command-palette-search-input/index.tsx';
import { components } from '../../../../common/ui/components/index.tsx';
import { OutlinedLozenge, SearchLozenge } from '../../../../common/ui/custom-lozenge/index.tsx';
import {
	getAnalyticsAttributesFromResult,
	getQueryHash,
	getRegistryData,
	getWordCount,
	isSecondaryTrigger,
} from '../../../../common/utils.tsx';
import { getActiveCommandList } from '../../../../common/utils/get-active-command-list/index.tsx';
import { useCommandPalette } from '../../../../controllers/command-palette/index.tsx';
import {
	COMMAND_MODE_TEST_ID,
	COMMAND_BACK_PARENT_TEST_ID,
	SPINNER_CONTAINER_TEST_ID,
	SEARCH_LOZENGE_TEST_ID,
} from './constants.tsx';
import messages from './messages.tsx';
import type { HeaderProps } from './types.tsx';

export const Header = ({
	defaultAriaLabel,
	defaultPlaceholder,
	results,
	activeCommand,
}: HeaderProps) => {
	const { formatMessage } = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [
		{
			search,
			forceSearchFocus,
			isOpen,
			expandedCommand,
			activeIndex,
			loadingList,
			onAnalytics,
			registry,
			activeRegistry,
			shouldForceSearchValue,
		},
		{
			handleSearch,
			goBackLevel,
			forceFocusSearch,
			executeCommand,
			setFocusInput,
			resetShouldForceSearchValue,
		},
	] = useCommandPalette();

	const textFieldRef = useRef<HTMLInputElement>();

	const onFocusInput = (isFocused: boolean) => () => {
		setFocusInput(isFocused);
	};

	useEffect(() => {
		if (shouldForceSearchValue && textFieldRef.current && textFieldRef.current.value !== search) {
			resetShouldForceSearchValue();
			textFieldRef.current.value = search;
		}
	}, [resetShouldForceSearchValue, search, shouldForceSearchValue]);

	const onInputKeyPress = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Backspace' && search === '') {
				goBackLevel(false, {
					createAnalyticsEvent,
					method: 'keyboard',
				});
			}
			if (event.key === 'Enter') {
				event.preventDefault();
				const listed: ListedCommand | undefined = results.find(
					(result): result is ListedCommand => !result.isGroup && result.index === activeIndex,
				);

				if (listed) {
					executeCommand(
						listed.command,
						{
							method: 'keyboard',
							createAnalyticsEvent,
							attributes: {
								...getAnalyticsAttributesFromResult(listed),
								...getRegistryData(registry, activeRegistry, listed.command.id),
							},
						},
						isSecondaryTrigger(event),
					);
				}
			}
		},
		[
			search,
			goBackLevel,
			createAnalyticsEvent,
			results,
			activeIndex,
			executeCommand,
			registry,
			activeRegistry,
		],
	);

	const onTypeAnalytics = useCallback(
		(query: string) => {
			if (onAnalytics && createAnalyticsEvent) {
				const event = createAnalyticsEvent({});
				onAnalytics(event, TRACK_EVENT_TYPE, {
					action: 'entered',
					actionSubject: 'text',
					actionSubjectId: 'commandPaletteSearch',
					attributes: {
						wordCount: getWordCount(query),
						queryLength: query.length,
						queryHash: getQueryHash(query),
						queryVersion: -1, // This is done to prevent the quickSearch queryVersion being sent which may be confusing, something to reimplement later
					},
				});
			}
		},
		[createAnalyticsEvent, onAnalytics],
	);

	const onTypeAnalyticsDebounced = useMemo(() => debounce(onTypeAnalytics, 400), [onTypeAnalytics]);

	useEffect(
		() => () => {
			onTypeAnalyticsDebounced?.cancel?.();
		},
		[onTypeAnalyticsDebounced],
	);

	const onTypeSearch = useCallback(
		(ev: React.ChangeEvent<HTMLInputElement>) => {
			const searchValue = ev.target.value;
			handleSearch(searchValue);
			onTypeAnalyticsDebounced(searchValue);
		},
		[handleSearch, onTypeAnalyticsDebounced],
	);

	const handleBackButton = useCallback(() => {
		goBackLevel(false, {
			createAnalyticsEvent,
			method: 'mouse',
		});
	}, [createAnalyticsEvent, goBackLevel]);

	useEffect(() => {
		if (textFieldRef.current && forceSearchFocus) {
			textFieldRef.current.focus();
			forceFocusSearch(false);
		}
	}, [forceSearchFocus, forceFocusSearch]);

	useEffect(() => {
		if (isOpen && textFieldRef.current) {
			textFieldRef.current.focus();
		}
	}, [isOpen]);

	const renderLozenge = useCallback((command: CommandInternal) => {
		if (command.mode) {
			return (
				<OutlinedLozenge key={command.id} data-testid={COMMAND_MODE_TEST_ID}>
					&nbsp;{command.mode}&nbsp;
				</OutlinedLozenge>
			);
		}

		const searchLozengeName = command.searchLozengeName || command.name;
		return (
			<SearchLozenge key={command.id} data-testid={SEARCH_LOZENGE_TEST_ID}>
				{searchLozengeName}
			</SearchLozenge>
		);
	}, []);

	const elemBeforeInput = useMemo(() => {
		if (!expandedCommand)
			return <SearchIcon spacing="spacious" label="" color={token('color.icon.subtle')} />;

		return (
			<ElemBeforeInputWrapper>
				<Tooltip content={formatMessage(messages.goBack)}>
					<BackButton
						testId={COMMAND_BACK_PARENT_TEST_ID}
						appearance="subtle"
						onClick={handleBackButton}
					>
						<ChevronLeftLargeIcon
							LEGACY_size="medium"
							spacing="spacious"
							label={formatMessage(messages.goBack)}
							color={token('color.icon.subtle')}
						/>
					</BackButton>
				</Tooltip>
				<LozengeContainer>{renderLozenge(expandedCommand)}</LozengeContainer>
			</ElemBeforeInputWrapper>
		);
	}, [expandedCommand, formatMessage, handleBackButton, renderLozenge]);

	const elemAfterInput = useMemo(() => {
		if (expandedCommand && loadingList) {
			return (
				<SpinnerContainer data-testid={SPINNER_CONTAINER_TEST_ID}>
					<Spinner size="small" />
				</SpinnerContainer>
			);
		}

		return null;
	}, [expandedCommand, loadingList]);

	const placeholder = useMemo(() => {
		if (expandedCommand) return expandedCommand.placeholder || expandedCommand.name;
		return defaultPlaceholder;
	}, [expandedCommand, defaultPlaceholder]);

	const ModalHeader = useMemo(
		() => getActiveCommandList(expandedCommand)?.components?.Header || components.Header,
		[expandedCommand],
	);

	const ariaLabel = useMemo(() => {
		if (!expandedCommand && defaultAriaLabel) return defaultAriaLabel;
		return placeholder;
	}, [defaultAriaLabel, expandedCommand, placeholder]);

	return (
		<ModalHeader>
			<CommandPaletteSearchInput
				aria-label={ariaLabel}
				placeholder={placeholder}
				onKeyDown={onInputKeyPress}
				elemBeforeInput={elemBeforeInput}
				elemAfterInput={elemAfterInput}
				onChange={onTypeSearch}
				ref={textFieldRef}
				onFocus={onFocusInput(true)}
				onBlur={onFocusInput(false)}
				activeId={activeCommand?.id}
			/>
		</ModalHeader>
	);
};

export default Header;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BackButton = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	width: '24px !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	height: '24px !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	padding: '0 !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	svg: {
		marginLeft: '-5px',
	},
	'&:hover, &:focus': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		svg: {
			color: `${token('color.icon')}`,
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerContainer = styled.div({
	width: '24px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ElemBeforeInputWrapper = styled.div({
	display: 'flex',
	gap: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.050'),
	marginRight: token('space.025'),
});
